import {
  Group,
  GroupResponse,
  OrganisationResponse,
  PractitionerData,
} from 'api';
import { InviteUserOrgOptions } from 'types/types';
import {
  SvgCheckCircle1Bold,
  SvgRemoveCircleBold,
} from '@insights-ltd/design-library/components';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { styled } from '@mui/material';
import { PermissionRoleTextType } from 'pages/EditAccount/EditAccountForm/RolePermissionV2';
import { formatList } from 'utils/formatList';
import { FormData, PermissionRole } from './types';

export const permissionContent = {
  createAndManagerExperiences: {
    key: 'ui.event-management.users.invite.permission-roles.list-item.manage-experiences',
  },
  createAndManageLearners: {
    key: 'ui.event-management.users.invite.permission-roles.list-item.manage-learners',
  },
  createAndManageTeams: {
    key: 'ui.event-management.users.invite.permission-roles.list-item.manage-teams',
  },
  viewAllUsersAndUnitWallets: {
    key: 'ui.event-management.users.invite.permission-roles.list-item.view-all-users',
  },
  createAndManageEvaluatorLinks: {
    key: 'ui.event-management.users.invite.permission-roles.list-item.manage-evaluator-links',
  },
  transferUnits: {
    key: 'ui.event-management.users.invite.permission-roles.list-item.transfer-units',
  },
  purchaseProfilesAndChapters: {
    key: 'ui.event-management.users.invite.permission-roles.list-item.purchase-profiles',
  },
  createAndManageOrganisations: {
    key: 'ui.event-management.users.invite.permission-roles.list-item.manage-organisations',
    isRestricted: true,
  },
  accessGroupOrganisationsWithinSameGroup: {
    key: 'ui.event-management.users.invite.permission-roles.list-item.access-group-organisations',
    isRestricted: true,
  },
};

const adu_GM_common = [
  permissionContent.createAndManagerExperiences,
  {
    key: 'ui.event-management.users.invite.permission-roles.list-item.transfer-learners',
  },
  {
    key: 'ui.event-management.users.invite.permission-roles.list-item.multiple-organisations',
  },
  permissionContent.viewAllUsersAndUnitWallets,
  permissionContent.createAndManageEvaluatorLinks,
  permissionContent.transferUnits,
  permissionContent.purchaseProfilesAndChapters,
];

export const userPermissionContent: PermissionRoleTextType = {
  STANDARD: [
    permissionContent.createAndManagerExperiences,
    permissionContent.createAndManageLearners,
    permissionContent.createAndManageTeams,
    permissionContent.viewAllUsersAndUnitWallets,
    permissionContent.createAndManageEvaluatorLinks,
    permissionContent.transferUnits,
    permissionContent.purchaseProfilesAndChapters,
    permissionContent.createAndManageOrganisations,
    permissionContent.accessGroupOrganisationsWithinSameGroup,
  ],
  RESTRICTED_USER: [
    permissionContent.createAndManagerExperiences,
    permissionContent.createAndManageLearners,
    permissionContent.createAndManageTeams,
    permissionContent.viewAllUsersAndUnitWallets,
    permissionContent.createAndManageEvaluatorLinks,
    { ...permissionContent.transferUnits, isRestricted: true },
    { ...permissionContent.purchaseProfilesAndChapters, isRestricted: true },
    { ...permissionContent.createAndManageOrganisations, isRestricted: true },
    {
      ...permissionContent.accessGroupOrganisationsWithinSameGroup,
      isRestricted: true,
    },
  ],
  ADVANCED_USER: [
    ...adu_GM_common,
    {
      key: 'ui.event-management.users.invite.permission-roles.list-item.manage-own-group-organisations',
    },
    {
      ...permissionContent.accessGroupOrganisationsWithinSameGroup,
      isRestricted: true,
    },
  ],
  GROUP_MANAGER: [
    ...adu_GM_common,
    {
      key: 'ui.event-management.users.invite.permission-roles.list-item.manage-all-group-organisations',
    },
    {
      key: 'ui.event-management.users.invite.permission-roles.list-item.access-all-group-organisations',
    },
  ],
  CLIENT_SERVICES: [
    permissionContent.createAndManagerExperiences,
    {
      key: 'ui.event-management.users.invite.permission-roles.list-item.manage-transfer-delete-learners',
    },
    {
      key: 'ui.event-management.users.invite.permission-roles.list-item.multiple-organisations',
    },
    {
      key: 'ui.event-management.users.invite.permission-roles.list-item.view-all-users',
    },
    {
      key: 'ui.event-management.users.invite.permission-roles.list-item.manage-evaluator-links',
    },
    {
      key: 'ui.event-management.users.invite.permission-roles.list-item.manage-units',
    },
    {
      key: 'ui.event-management.users.invite.permission-roles.list-item.manage-all-organisations-groups',
    },
    {
      key: 'ui.event-management.users.invite.permission-roles.list-item.edit-delete-all-organisations-groups',
    },
    {
      key: 'ui.event-management.users.invite.permission-roles.list-item.invite-new-users',
    },
  ],
};

export const permissionContentTitleKeys: Record<PermissionRole, string> = {
  STANDARD:
    'ui.event-management.users.invite.permission-roles.standard-user.intro-text',
  RESTRICTED_USER:
    'ui.event-management.users.invite.permission-roles.restricted-user.intro-text',
  ADVANCED_USER:
    'ui.event-management.users.invite.permission-roles.advanced-user.intro-text',
  GROUP_MANAGER:
    'ui.event-management.users.invite.permission-roles.group-manager.intro-text',
  CLIENT_SERVICES:
    'ui.event-management.users.invite.permission-roles.client-services.intro-text',
};
export const AllowedPermissionIcon = styled(SvgCheckCircle1Bold)(
  ({ theme }) => ({
    marginTop: theme.spacing(spacingSizeMap.XS),
    fill: theme.palette.success.main,
  }),
);

export const RestrictedPermissionIcon = styled(SvgRemoveCircleBold)(
  ({ theme }) => ({
    marginTop: theme.spacing(spacingSizeMap.XS),
    fill: theme.palette.error.main,
  }),
);

export const getDefaultSelectedProducts = (
  options: { disabled?: boolean; defaultSelected?: boolean; value: string }[],
) =>
  options
    .filter((option) => option.defaultSelected)
    .map((product) => product.value);

export const updateCurrentOrg = (
  currentOrganisation: OrganisationResponse | undefined,
  setCurrentOrg: (currentOrganisation: OrganisationResponse) => void,
  practitionerOrganisations: OrganisationResponse[],
) => {
  if (
    currentOrganisation &&
    !practitionerOrganisations.some(({ id }) => id === currentOrganisation.id)
  ) {
    setCurrentOrg(currentOrganisation);
  }
};

export const getNavigatePath = (
  selectedGroup: Group | null,
  requestData: FormData & { organisationIds: string[] },
  existingUser = false,
) => {
  if (selectedGroup) {
    return { path: `/groups/${selectedGroup.id}`, activeTab: null };
  }
  return {
    path: `/organisations/${requestData.organisationIds[0]}`,
    activeTab: existingUser ? 'practitioners' : 'invites',
  };
};

export const getUserOptions = (
  t: (key: string) => string,
  existingPractitioner: PractitionerData,
  isGroupManager: boolean,
) => {
  if (existingPractitioner && isGroupManager) {
    return [
      {
        label: t(
          'ui.event-management.users.invite.select-user-option.gm.single-org',
        ),
        value: InviteUserOrgOptions.ONE_ORG,
      },
      {
        label: t(
          'ui.event-management.users.invite.select-user-option.gm.multi-org',
        ),
        value: InviteUserOrgOptions.MULTI_ORG,
      },
    ];
  }
  return [
    {
      label: t(
        'ui.event-management.users.invite.select-user-option.single-org',
      ),
      value: InviteUserOrgOptions.ONE_ORG,
    },
    {
      label: t('ui.event-management.users.invite.select-user-option.multi-org'),
      value: InviteUserOrgOptions.MULTI_ORG,
    },
  ];
};

export const useGetWarningModalContent = ({
  selectedOrgs,
  groups,
}: {
  selectedOrgs: OrganisationResponse[];
  groups: GroupResponse[];
}) => {
  const selectedOrgIds = selectedOrgs?.map((org) => org.id) ?? [];
  const selectedOrgGroup = groups.find((group) =>
    group.organisations.some((org) => selectedOrgIds.includes(org.id)),
  );

  const selectedOrgNamesList =
    selectedOrgGroup?.organisations
      .filter(({ id }) => selectedOrgIds.includes(id))
      .map(({ name }) => name) ?? [];
  const formattedOrgNames = selectedOrgNamesList.length
    ? formatList(selectedOrgNamesList, 'en-gb')
    : '';

  return { selectedOrgGroup, formattedOrgNames };
};
